import { Fragment, useCallback, useRef, useEffect, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { styled } from '@mui/material'

import { useTranslation } from 'src/utils/i18n'
import { setBlock, androidLocalRefresh } from 'src/@api/android'
import { getLocalAndroidAll } from 'src/@api/dashboard'
import { useDispatch } from 'react-redux'
import { setCurrentTab } from 'src/store/apps/Common'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'
import { readLocalDeviceUdid, readUserEmail } from 'src/utils/readAndroidLocalContext'
import ContentLoading from 'src/components/ContentLoading'

// 自定义隐藏滚动条且保留滚动特性(div
const ScrollDiv = styled('div')({
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

// 自定义滚动条(ul
const ScrollUlShow = styled('ul')({
  '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
  scrollbarWidth: 'thin',  /* 适用于 Firefox，设置滚动条为细 */
  '&::-webkit-scrollbar': {
    width: '4px',  /* 设置滚动条的宽度为 4px */
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#888',  /* 设置滚动条滑块的颜色 */
    borderRadius: '10px',  /* 设置滑块圆角 */
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',  /* 设置滚动条轨道的颜色 */
  },
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

const BlockWebsites = () => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const [allData, setAllData] = useState([])
  const [showData, setShowData] = useState([]) // 展示的数据
  const [allHistory, setAllHistory] = useState([])
  const [showHistory, setShowHistory] = useState([])
  const [ruleValue, setRuleValue] = useState('') // 输入的规则值
  const [ruleFlag, setRuleFlag] = useState(0) // 输入框是否为空
  const [isOpen, setIsOpen] = useState(false) // 移动端是否展示输入框
  const [isOpenHistory, setIsOpenHistory] = useState(false) // 是否展开显示浏览器记录
  const [searchValue, setSearchValue] = useState('')
  const leftRef = useRef(null)
  const [height, setHeight] = useState(0)
  const [isLoading, setIsLoading] = useState(true) //正在加载获取数据

  useEffect(() => {
    if (leftRef.current) {
      setHeight(leftRef.current.offsetHeight) // 获取高度
    }
  }, [isOpen])

  // 分页参数
  const [pagination, setPagination] = useState({
    reqNum: 100, // 单次查询数量
    nowOffset: 0, // 偏移量
    maxNum: false // 全部数据查询完毕
  })

  const mergeUniqueByUrl = (old, next) =>
    Array.from(new Map([...old, ...next].map(item => [item.url, item])).values())

  // 获取浏览器记录数据
  const getHistoryData = useCallback(async (nowOffset, deviceUdid, { isCurrent, replace = false } = {}) => {
    const response = await getLocalAndroidAll({
      username: readUserEmail(),
      params: {
        category: 'CHROME_HISTORY',
        offset: nowOffset,
        limit: pagination.reqNum,
        deviceUdid
      }
    })

    if (isCurrent && !isCurrent()) return

    if (response.data.contents == null) {
      setPagination(page => ({ ...page, maxNum: true }))

      return
    }

    const contents = response.data.contents
    setAllHistory(old => mergeUniqueByUrl(replace ? [] : old, contents))
    setShowHistory(old => mergeUniqueByUrl(replace ? [] : old, contents))
  }, [pagination.reqNum])

  const loadBlockedSites = useCallback(async (deviceUdid, { isCurrent } = {}) => {
    const result = await setBlock({ device_id: deviceUdid })

    if (isCurrent && !isCurrent()) return

    const newBlockData = result.data.website
    setAllData(newBlockData.map(item => ({ name: item })))
    setShowData(newBlockData.map(item => ({ name: item })))
    setIsLoading(false)
  }, [])

  const reloadPageData = useCallback(
    async (deviceUdid, { signal, isCurrent } = {}) => {
      setIsLoading(true)
      setAllData([])
      setShowData([])
      setAllHistory([])
      setShowHistory([])
      setRuleValue('')
      setSearchValue('')
      setPagination({ reqNum: 100, nowOffset: 0, maxNum: false })

      await Promise.all([
        loadBlockedSites(deviceUdid, { isCurrent }),
        getHistoryData(0, deviceUdid, { isCurrent, replace: true })
      ])
    },
    [getHistoryData, loadBlockedSites]
  )

  // 设备切换时重置屏蔽站点与操作历史
  useDeviceScopedInit(
    useCallback(
      (_, scope) => {
        const deviceUdid = readLocalDeviceUdid(scope.context)
        if (!deviceUdid) return

        reloadPageData(deviceUdid, scope)
      },
      [reloadPageData]
    )
  )

  const isBlock = async ({ name, action }) => {
    const deviceUdid = readLocalDeviceUdid()
    const result = await setBlock({ device_id: deviceUdid, type: 'website', name, action })
    await androidLocalRefresh({ device_id: deviceUdid, type: 51, limit: action, url: name })
    const newBlockData = result.data.website
    setAllData(newBlockData.map(item => ({ name: item })))
  }

  useEffect(() => {
    const results = allData.filter(item => { return item.name.toLowerCase().includes(searchValue.toLowerCase()) })
    setShowData(results)
  }, [searchValue, allData])

  useEffect(() => {
    const results = allHistory.filter(item => { return item.url.toLowerCase().includes(ruleValue.toLowerCase()) })
    setShowHistory(results)
  }, [ruleValue, allHistory])

  const dom = useRef(null)
  const [scroll, setScroll] = useState(0) // 滚动百分比

  // 获取滚动百分比
  useEffect(() => {
    const handScroll = () => {
      if (dom.current) {
        const { scrollTop, scrollHeight, clientHeight } = dom.current
        const progress = (scrollTop / (scrollHeight - clientHeight)) * 100
        setScroll(progress.toFixed(0))
      }
    }
    const currentRef = dom.current
    if (currentRef) {
      currentRef.addEventListener('scroll', handScroll)
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handScroll)
      }
    }
  }, [])

  // 监听滚动百分比
  useEffect(() => {
    if (scroll >= 95) {
      const nowOffset = pagination.nowOffset + pagination.reqNum
      setPagination(page => ({ ...page, nowOffset }))
      if (!pagination.maxNum) {
        getHistoryData(nowOffset, readLocalDeviceUdid())
      }
    }
  }, [scroll, getHistoryData, pagination.maxNum, pagination.nowOffset, pagination.reqNum])

  useEffect(() => {
    const title = t('Block Website')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  // 埋点上报
  useEffect(() => {
    if (allData.length === 0) return
    const events = allData
      .map(item => ({
        event_name: 'Click_Block',
        properties: {
          limit: item?.name || '',
        }
      }))
    if (events.length > 0) {
    }
  }, [allData])

  return (
    <Fragment>
      {/* 展示页 */}
      <ScrollDiv className='w-full !h-[calc(var(--vh2)*100-215px)] smj:!h-[calc(var(--vh2)*100-90px)] [box-shadow:0px_0px_12px_0px_#00000021] rounded-[10px] overflow-auto bg-white font-[Poppins] relative'>

        <div className='w-full h-full flex smj:flex-col'>
          {/* 左侧新增规则 */}
          <div className='w-full h-auto smjs:p-[36px_30px_0px_30px] smj:px-[16px] smjs:[border-right:1px_solid_#DCDCDC] smjs:max-w-[520px]' ref={leftRef}>

            <div className='w-full smjs:h-[206px] flex flex-col smjs:justify-start items-center px-[30px] smj:px-[16px] [border:1px_solid_#DCDCDC] rounded-[8px] smj:mt-[16px]'>
              <div className='flex justify-between items-center w-full smjs:mt-[24px] mt-[8px] mb-[18px] smj:mb-3'>
                <div className='text-[16px] smjs:text-[#000000] smj:text-[#666CFF] font-[600] leading-[24px]'>{t('Add Rule')}</div>
                <Image src={isOpen ? '/image/website/less.svg' : '/image/website/more.svg'} width={16} height={16} alt='' className='smjs:hidden cursor-pointer'
                  onClick={() => { setIsOpen(!isOpen) }} />
              </div>
              <div className='w-full relative'>
                <input
                  id='ruleAddress' type='text' placeholder={t('Enter website address')} value={ruleValue}
                  className={`w-full h-[40px] smj:h-[36px] bg-[#F4F5F6] rounded-[8px] outline-none relative ${isOpen || 'smj:hidden'}
                            ${ruleFlag === 1 ? 'shadow-[inset_0px_0px_0px_1px_red] placeholder:text-red-500 caret-red-500' : 'focus:shadow-[inset_0px_0px_0px_1px_#666CFF] caret-[#666CFF]'}
                            px-[18px] smj:px-[13px] text-[12px] text-[#222222] placeholder:text-[#9E9E9E] leading-[21px] smj:leading-[18px]`}
                  onChange={e => {
                    if (e.target.value.trim() !== '') setRuleFlag(0)
                    setRuleValue(e.target.value)
                  }}
                  onFocus={() => setIsOpenHistory(true)}
                  onBlur={() => setIsOpenHistory(false)}
                />
                <div className={`absolute top-11 smj:top-10 z-[10] w-full bg-white rounded-[6px] shadow-[0px_0px_3px_0px_#0000001A] p-[4px_18px_0px_18px] smj:p-[4px_12px_0px_12px] smjs:!max-h-[200px] overflow-auto ${isOpenHistory || 'hidden'} ${showHistory.length > 0 || '!p-0'}`}
                  style={{ maxHeight: `calc(${height}px - 102px)` }} ref={dom}>
                  {showHistory.map((item, i) => {
                    return (
                      <div key={i} className='py-1 text-[12px] text-[#222222CC] leading-[24px] line-clamp-1 overflow-hidden border-b-[1px] border-[#2222221A] cursor-pointer'
                        onMouseDown={() => setRuleValue(item.url)}>
                        {item.url}
                      </div>
                    )
                  })}
                </div>
              </div>
              <button
                className={`w-[180px] h-[40px] smj:w-[100px] smj:h-[35px] text-[12px] text-[#000000] font-[600] leading-[18px] bg-[#FFCF55] rounded-[30px] my-[30px] smj:m-[16px_0_13px_auto] ${isOpen || 'smj:hidden'}`}
                onClick={() => {
                  if (ruleValue === '' || allData.some(item => item.name === ruleValue)) {
                    document.getElementById('ruleAddress').focus()
                    setRuleFlag(1)
                  }
                  else { isBlock({ name: ruleValue, action: true }) }
                }}
              >{t('Block')}</button>
              {/* 移动端tips文案 */}
              <div className={`text-[12px] text-[#9E9E9E] font-[400] leading-[18px] smjs:hidden mb-4 ${isOpen || 'smj:hidden'}`}>
                <p>{t('Tips')}:</p>
                <p>1. {t('You can block a single page or an entire website')}</p>
                <p>2. {t('Blocking an address will also block all pages under it e')}</p>
              </div>
            </div>
            {/* pc端tips文案 */}
            <div className='text-[12px] text-[#9E9E9E] font-[400] leading-[18px] smj:hidden mt-4'>
              <p>{t('Tips')}:</p>
              <p>1. {t('You can block a single page or an entire website')}</p>
              <p>2. {t('Blocking an address will also block all pages under it e')}</p>
            </div>
          </div>

          {/* 右侧展示添加过的规则 */}
          <div className='w-full p-[36px_30px_0px_30px] smj:p-[24px_16px_0px_16px]'>
            <div className='flex smjs:justify-between smj:flex-col smjs:items-center mb-[12px] smj:mb-[6px]'>
              <div className='text-[16px] text-[#000000] font-[600] leading-[24px] smj:mb-[10px]'>{t('Blocked Sites')}</div>
              <div className="relative h-[36px]">
                <input className="w-[280px] smj:w-full text-[14px] leading-[20px] placeholder:text-[#9E9E9E] text-[#222222] bg-[#F4F5F6] border-[1px] border-[#F4F5F6] focus:border-[#666CFF] focus:outline-none rounded-[8px] font-[400] pl-4 pr-10 py-2"
                  placeholder={t('Search')}
                  onInput={(e) => { setSearchValue(e.target.value) }} />
                <div className='absolute top-0 right-0 flex items-center h-full pr-4'>
                  <Image src="/search.svg" className='w-[20px] h-[20px]' width={20} height={20} alt='Search' />
                </div>
              </div>
            </div>
            <ScrollUlShow className={`w-full smjs:!max-h-[666px] overflow-auto`}
              style={{ maxHeight: `calc(var(--vh2) * 100 - 190px - ${height}px)` }}>
              {showData.map((item, index) => (
                <li key={index} i className='text-[16px] text-[#000000] font-[400] leading-[24px] break-all [border-bottom:1px_solid_#E5E7EB]'>
                  <div className='w-full flex justify-between items-center py-[18px]'>
                    <div className='max-w-[78%] flex items-center gap-x-[12px] smj:gap-x-[10px]'>
                      <Image src={'/websites.svg'} alt={''} width={24} height={24} className='w-[24px] h-[24px]' />
                      <p>{item.name}</p>
                    </div>

                    <button
                      className='w-auto p-[4px_11px_4px_11px] text-[12px] text-[#000000] font-[400] leading-[18px] bg-[#FFCF55] rounded-[15px]'
                      onClick={() => isBlock({ name: item.name, action: false })}
                    >{t('Unblock')}</button>
                  </div>
                </li>
              ))}
            </ScrollUlShow>
            {isLoading ? (
        <div className='absolute inset-0 z-[20] bg-[#FFF] rounded-[10px]'>
          <ContentLoading />
        </div>
      ) : null}
{(allData.length > 0 || isLoading) ||
              <div className='text-[#222222E5] text-[15px] mt-2'>{t('No websites have been blocked yet')}</div>}
          </div>
        </div>
      </ScrollDiv>
    </Fragment>
  )
}

export default BlockWebsites


